// 261005 E2-P2a — MỌI quyết định hiển thị của khung Trợ lý AI nằm ở đây (hàm thuần, có jest). KHÔNG import antd
// (antd v6 + react-router v7 không load được dưới jest của react-scripts — xem CLAUDE.md). Dùng lại `episodeFixUtils.ts`, không chép.
import type { EpisodeFixMessage, EpisodeScriptRecord } from '../../../../types/adaptation';
import type {
  AssistantAction, AssistantBlock, AssistantInteraction, AssistantThread, AssistantUiStage, PreviewStarted, ScopeView, SkeletonIssueChip, ThreadDetail,
  LocalizeIssueChip,
} from '../../../../types/adaptationAssistant';
import type { EpisodeFixIssueChip } from '../../../../types/adaptation';
import type { WorkbenchStageKey } from '../../../../types/adaptationWorkbench';
import { SOURCE_SEND_WAIT_TEXT } from './sourceBlockView';
import { impactLines } from '../../sourceChapterUtils';
import { gateLabel, humanizeCodes } from '../../adaptationUtils';
import {
  MAX_FIX_TEXT, PreviewPhase, PreviewView, RelinkView, editBlockerText, fixErrorText, previewView,
} from '../../episodeFixUtils';
import { friendlyJobNote, GENERIC_JOB_ERROR_HINT, looksTechnical } from '../jobErrorText';
import { redactMoney } from '../moneyRedact';
import { isOverviewWriterCode, overviewErrorView, overviewNotEffectiveLines } from '../overview/overviewErrors';
import { FIX_LABEL, type AssistantRequest } from './assistantIntent';
import { autoApplyNote, type AutoApplyNote } from './autoApplyView';

// 261015 P5a (D5): `autoApplyNote` ở `autoApplyView.ts` (thuần, không vòng phụ thuộc); re-export để mọi chỗ import từ `assistantView`.
export { autoApplyNote, autoApplyToastText, undoConfirmText } from './autoApplyView';

const str = (v: unknown): string => String(v ?? '').trim();

// ── scope ────────────────────────────────────────────────────────────────────────────────────

export type AssistantScope =
  | { supported: true; stageKey: AssistantUiStage; scopeKey: string; /** ⑤: số tập; ② / ④ (cả truyện): null. */ episode: number | null }
  | { supported: false; reason: string };

/**
 * Khung nói về cái gì. ② Khung xương = cả truyện (`scopeKey = ''`). ⑤ chỉ khi một tập CÓ kịch bản.
 * `scriptEpisode` = tập ⑤ báo lên (chỉ khi có kịch bản + workbench đã tải); `noScriptEpisode` = tập đang chọn nhưng chưa có kịch bản (R3).
 */
export const assistantScopeOf = (stage: WorkbenchStageKey, scriptEpisode: number | null, noScriptEpisode: number | null = null): AssistantScope => {
  if (stage === 'skeleton') return { supported: true, stageKey: 'skeleton', scopeKey: '', episode: null };
  if (stage === 'split') return { supported: true, stageKey: 'split', scopeKey: '', episode: null };
  if (stage === 'source') return { supported: true, stageKey: 'source', scopeKey: '', episode: null };   // E3-P4b1
  if (stage === 'localize') return { supported: true, stageKey: 'localize', scopeKey: '', episode: null };   // E3-P3b
  if (stage !== 'script') return { supported: false, reason: 'Trợ lý AI chưa hỗ trợ bước này — hiện có ở bước Nguồn (①), Khung xương (②), Bản địa hoá (③), Chia tập (④) và Kịch bản (⑤).' };
  if (scriptEpisode === null) {
    if (noScriptEpisode !== null) return { supported: false, reason: `Tập ${noScriptEpisode} chưa có kịch bản — viết tập trước rồi trò chuyện.` };
    return { supported: false, reason: 'Chọn một tập ở danh sách để trò chuyện.' };
  }
  return { supported: true, stageKey: 'script', scopeKey: `ep:${scriptEpisode}`, episode: scriptEpisode };
};

/** Chữ của khung theo chặng (một chỗ, có test): thẻ "Đang nói về", gợi ý đầu luồng, placeholder ô soạn, tiêu đề tab. */
export const scopeTexts = (scope: Extract<AssistantScope, { supported: true }>): { about: string; intro: string; placeholder: string; chipsLabel: string; unit: string } =>
  scope.stageKey === 'localize'
    ? {
      about: 'Đang nói về: Bản địa hoá', unit: 'bản địa hoá',
      intro: 'Hỏi hoặc nhờ sửa tên trong phim (đổi tên, đổi họ cả nhà, khoá/mở khoá, từ vựng). Nêu rõ nhân vật nào. Mình trả lời và đề xuất thay đổi có sẵn xem thử để bạn bấm Áp — đổi tên sẽ tự thay trong kịch bản đã viết. Làm lại từ đầu và bối cảnh phim thì bạn tự bấm ở thanh ③.',
      placeholder: 'Hỏi hoặc nhờ sửa tên… (Enter gửi, Shift+Enter xuống dòng)', chipsLabel: 'Vấn đề của bản địa hoá — tick để AI đưa phương án đúng chỗ:',
    }
    : scope.stageKey === 'source'
    ? {
      about: 'Đang nói về: Nguồn truyện', unit: 'nguồn truyện',
      intro: 'Hỏi hoặc nhờ xử lý chương lỗi (tải lại từ link, tách, gộp với chương sau, xoá). Nêu rõ chương nào. Mình trả lời, đề xuất lệnh có sẵn ảnh hưởng để bạn bấm Áp — mình không viết lại chữ chương.',
      placeholder: 'Hỏi hoặc nhờ xử lý chương lỗi… (Enter gửi, Shift+Enter xuống dòng)', chipsLabel: 'Chương có vấn đề — tick để AI đưa phương án đúng chỗ:',
    }
    : scope.stageKey === 'split'
    ? {
      about: 'Đang nói về: Chia tập', unit: 'chia tập',
      intro: 'Hỏi hoặc nhờ sửa cách chia tập (tách, gộp, dời điểm cắt, sửa tóm tắt/móc, viết lại tóm tắt). Nêu rõ tập nào. Mình trả lời, đề xuất thay đổi có sẵn xem thử để bạn bấm Áp, hoặc nói thật nếu chưa làm được.',
      placeholder: 'Hỏi hoặc nhờ sửa cách chia tập… (Enter gửi, Shift+Enter xuống dòng)', chipsLabel: 'Lỗi chia tập & tóm tắt cần xem lại — tick để AI sửa đúng chỗ:',
    }
    : scope.stageKey === 'skeleton'
    ? {
      about: 'Đang nói về: Khung xương', unit: 'khung xương',
      intro: 'Hỏi hoặc nhờ sửa khung xương (nhân vật, bối cảnh, cao trào, số tập…). Mình trả lời, đề xuất thay đổi có sẵn xem thử để bạn bấm Áp, hoặc nói thật nếu chưa làm được.',
      placeholder: 'Hỏi hoặc nhờ sửa khung xương… (Enter gửi, Shift+Enter xuống dòng)', chipsLabel: 'Cảnh báo của khung xương — tick để AI sửa đúng chỗ:',
    }
    : {
      about: `Đang nói về: Tập ${scope.episode}`, unit: `tập ${scope.episode}`,
      intro: `Hỏi hoặc nhờ sửa tập ${scope.episode}. Mình trả lời, đề xuất thay đổi để bạn xem rồi áp, hoặc nói thật nếu chưa làm được.`,
      placeholder: 'Hỏi hoặc nhờ sửa tập này… (Enter gửi, Shift+Enter xuống dòng)', chipsLabel: 'Lỗi của tập — tick để AI sửa đúng chỗ:',
    };

/** Chip theo chặng (kiểu union ở `ScopeView.chips`). */
export const scriptChipsOf = (view: Pick<ScopeView, 'chips'> | null | undefined): EpisodeFixIssueChip[] => (view?.chips ?? []) as EpisodeFixIssueChip[];
export const skeletonChipsOf = (view: Pick<ScopeView, 'chips'> | null | undefined): SkeletonIssueChip[] => (view?.chips ?? []) as SkeletonIssueChip[];
/** Chip ① (cùng dạng chip ②): finding của chương, `id = raw:<chapterId>:<code>`, `refs = [orderIndex]`. */
export const sourceChipsOf = (view: Pick<ScopeView, 'chips'> | null | undefined): SkeletonIssueChip[] => (view?.chips ?? []) as SkeletonIssueChip[];
/** Nhãn chip ①: "Chương 12: …" (không in mã gate thô). */
export const sourceChipLabel = (c: Pick<SkeletonIssueChip, 'severity' | 'message' | 'refs'>): string => `${c.severity === 'ERROR' ? '⛔ ' : ''}${c.refs.length ? `Chương ${c.refs[0]}: ` : ''}${c.message}`;

/** Chip ③ (E3-P3b): chip ② + `kind`/`term`/`problem` (BE tính id; FE chỉ đọc). */
export const localizeChipsOf = (view: Pick<ScopeView, 'chips'> | null | undefined): LocalizeIssueChip[] => (view?.chips ?? []) as LocalizeIssueChip[];
/** Nhãn chip ③: vi phạm in mã gate; còn lại (cổng AI / chưa ánh xạ / chưa có tên) chỉ in nội dung. */
export const localizeChipLabel = (c: Pick<LocalizeIssueChip, 'kind' | 'gate' | 'severity' | 'message' | 'refs'>): string =>
  `${c.severity === 'ERROR' ? '⛔ ' : ''}${c.kind === 'violation' ? `${gateLabel(c.gate)} — ` : ''}${humanizeCodes(c.message)}`;

/** Chip ④ (cùng dạng chip ②): vi phạm thuộc nhóm tập + `TEXT_REVIEW` (`textReview:<ep>`). */
export const splitChipsOf = (view: Pick<ScopeView, 'chips'> | null | undefined): SkeletonIssueChip[] => (view?.chips ?? []) as SkeletonIssueChip[];
export const chipIdsOf = (view: Pick<ScopeView, 'chips'> | null | undefined): string[] => (view?.chips ?? []).map((c) => c.id);

/** Màu tag của chip ②: ERROR đỏ, còn lại xám. */
export const skeletonChipLabel = (c: Pick<SkeletonIssueChip, 'gate' | 'severity' | 'message'>): string => `${c.severity === 'ERROR' ? '⛔ ' : ''}${gateLabel(c.gate)} — ${humanizeCodes(c.message)}`;

// ── ô soạn ───────────────────────────────────────────────────────────────────────────────────

export interface ComposerState { show: boolean; enabled: boolean; reason: string | null }

/** Gửi tin chỉ ĐỌC dữ liệu (không giữ khoá) ⇒ KHÔNG tắt vì `ctx.busy`. Tập không nháp ⇒ khung chỉ xem: ẩn ô soạn + lý do. */
export const composerState = (o: {
  view: Pick<ScopeView, 'editBlocker'> | null; sending: boolean; runningInteraction: boolean; text: string; selected: readonly string[]; stageKey?: AssistantUiStage;
}): ComposerState => {
  if (!o.view) return { show: true, enabled: false, reason: 'Đang tải…' };
  if (o.view.editBlocker) return { show: false, enabled: false, reason: editBlockerText(o.view.editBlocker) ?? 'Tập này không sửa được' };
  if (o.sending || o.runningInteraction) return { show: true, enabled: false, reason: o.stageKey === 'source' ? SOURCE_SEND_WAIT_TEXT : 'Đang chờ AI trả lời…' };
  const text = str(o.text);
  if (text.length > MAX_FIX_TEXT) return { show: true, enabled: false, reason: `Yêu cầu tối đa ${MAX_FIX_TEXT} ký tự` };
  if (!text && !o.selected.length) return { show: true, enabled: false, reason: 'Gõ yêu cầu hoặc tick một lỗi' };
  return { show: true, enabled: true, reason: null };
};

// ── khối thay đổi ────────────────────────────────────────────────────────────────────────────

export type BlockPhase = PreviewPhase | 'proposed' | 'restored';

export interface BlockCardView extends Omit<PreviewView, 'phase'> {
  phase: BlockPhase;
  /** Nút "Xem thử" / "Xem thử lại". */
  canPreview: boolean;
  previewLabel: string;
  /** PROPOSED: cách làm + cảnh + lý do; APPLIED: chỉ sang mục Phiên bản. Rỗng nếu không có gì thêm. */
  detail: string;
  /** 261005 E2-P3 — APPLIED: nút "Khôi phục bản trước" (bật khi kịch bản hiện tại CHÍNH là bản khối này tạo ra); `undoReason` = lý do tắt (null khi bật / không áp dụng). */
  canUndo: boolean;
  undoReason: string | null;
  /** RESTORED: nút "Áp lại" (0 AI; server kiểm nội dung còn khớp, lệch ⇒ 409 CONTENT_DIVERGED ⇒ "Xem thử lại"). */
  canReapply: boolean;
  /** 261015 P5a (D5): "Đã tự áp…" / "Chưa tự áp: …" — null khi không có dấu tự áp. */
  autoNote: AutoApplyNote | null;
}

const UNDO_EPS_MS = 1;
export const UNDO_CHANGED_TEXT = 'Kịch bản đã có thay đổi mới hơn — khôi phục qua mục Phiên bản';

const BLOCK_TO_MESSAGE_STATUS: Record<string, EpisodeFixMessage['status']> = {
  PREVIEW_RUNNING: 'RUNNING', READY: 'READY', APPLIED: 'APPLIED', RESTORED: 'APPLIED', STALE: 'STALE', FAILED: 'FAILED', DISMISSED: 'DISMISSED',
};

/** "Khôi phục bản trước": bật khi `head.modifiedAt` (±1 ms) CHÍNH là mốc khối đã ghi; lệch ⇒ tắt kèm lý do. Máy chủ vẫn là cổng thật (409 SCRIPT_CHANGED). */
const undoState = (block: AssistantBlock, headModifiedAt: string | null | undefined, busy: boolean, busyReason?: string | null): { canUndo: boolean; undoReason: string | null } => {
  if (busy) return { canUndo: false, undoReason: busyReason || 'Đang có việc khác chạy — chờ xong rồi khôi phục.' };
  const applied = Date.parse(str(block.appliedResult?.modifiedAt));
  const head = Date.parse(str(headModifiedAt));
  if (Number.isNaN(head)) return { canUndo: false, undoReason: 'Đang tải kịch bản hiện tại…' };
  if (Number.isNaN(applied) || Math.abs(head - applied) > UNDO_EPS_MS) return { canUndo: false, undoReason: UNDO_CHANGED_TEXT };
  return { canUndo: true, undoReason: null };
};

/**
 * Ánh xạ `block.state` sang trạng thái mà `previewView` hiểu. Lỗi xem thử lấy từ `block.error` (cột riêng — `diff.error` không tồn tại, Y6).
 * `busy` ⇒ mọi nút GHI (Xem thử, Áp) tắt; "Bỏ" không bị khoá bởi `busy`.
 */
export const blockCardView = (block: AssistantBlock, nextEpisode: number, busy: boolean, headModifiedAt?: string | null, busyReason?: string | null): BlockCardView => {
  const autoNote = autoApplyNote(block);
  // 261015 P5a (D1/D5): Xem thử của khối AI sửa cảnh (không `command.op`) là JOB tốn AI ⇒ nhãn nói rõ; lệnh code (retitle…) xem thử đồng bộ $0 ⇒ giữ nhãn cũ.
  const aiCost = block.command?.op ? '' : ' (tốn AI)';
  const blank = { tone: 'neutral' as const, criticLabel: '', violationsLabel: '', wordsLabel: '', warnings: [] as string[], canUndo: false, undoReason: null as string | null, canReapply: false, autoNote };
  if (block.state === 'PROPOSED') {
    const targets = block.command?.targetScenes ?? [];
    const detail = [
      str(block.command?.approach),
      targets.length ? `Cảnh ${targets.join(', ')}` : '',
      str(block.command?.rationale) ? `Lý do: ${str(block.command.rationale)}` : '',
    ].filter(Boolean).join(' · ');
    return { ...blank, phase: 'proposed', canApply: false, canDismiss: true, headline: block.title, detail, canPreview: !busy, previewLabel: `Xem thử${aiCost}` };
  }
  if (block.state === 'RESTORED') {
    const rv = block.appliedResult?.restoreVersionNo;
    return {
      ...blank, phase: 'restored', canApply: false, canDismiss: true,
      headline: typeof rv === 'number' ? `Đã gỡ khỏi kịch bản (v${rv})` : 'Đã gỡ khỏi kịch bản',
      detail: 'Bản trước đã được khôi phục. "Áp lại" không tốn AI khi kịch bản chưa đổi; nếu đã đổi thì cần "Xem thử lại".',
      canPreview: !busy, previewLabel: `Xem thử lại${aiCost}`, canReapply: !busy,
    };
  }
  const status = BLOCK_TO_MESSAGE_STATUS[block.state] ?? null;
  const pseudo = { status, payload: { ...(block.diff ?? {}), ...(block.error ? { error: block.error } : {}) } } as unknown as EpisodeFixMessage;
  const pv = previewView(pseudo, nextEpisode);
  const retry = block.state === 'FAILED' || block.state === 'STALE';
  let { headline } = pv;
  const beforeTitle = str(block.diff?.beforeTitle);
  const afterTitle = str(block.diff?.afterTitle) || str(block.command?.episodeTitle);
  if (block.command?.op === 'retitle' && beforeTitle && afterTitle) headline = `${beforeTitle} → ${afterTitle}`;
  const metaHeadline = block.command?.op === 'restoreSceneMetadata' ? metaChangesHeadline((block.diff as { metaChanges?: unknown } | null | undefined)?.metaChanges) : null;
  if (metaHeadline) headline = metaHeadline;
  // 261015 P2b-3: chấp nhận gieo chỉ đổi biên lai (cảnh không đổi) — headline riêng, tránh "0 cảnh đổi" (F62).
  const plantHeadline = block.command?.op === 'acceptPlantWritten' ? plantAcceptanceHeadline((block.diff as { plantAcceptance?: unknown } | null | undefined)?.plantAcceptance) : null;
  if (plantHeadline) headline = plantHeadline;
  let detail = '';
  let undo: { canUndo: boolean; undoReason: string | null } = { canUndo: false, undoReason: null };
  if (block.state === 'APPLIED') {
    const v = block.appliedResult?.versionNo;
    headline = typeof v === 'number' ? `Đã áp (v${v})` : 'Đã áp';
    detail = 'Bản cũ vẫn còn ở mục Phiên bản.';
    undo = undoState(block, headModifiedAt, busy, busyReason);
  }
  // Xem thử xong mà thiếu thẻ trước/sau thì không có gì để duyệt ⇒ không cho áp.
  const canApply = pv.canApply && !busy && !(block.state === 'READY' && !block.diff);
  const canDismiss = block.state === 'STALE' ? true : pv.canDismiss;
  return { ...pv, ...undo, autoNote, canReapply: false, headline, detail, canApply, canDismiss, canPreview: retry && !busy, previewLabel: `${retry ? 'Xem thử lại' : 'Xem thử'}${aiCost}` };
};

// ── lượt chat ────────────────────────────────────────────────────────────────────────────────

/**
 * 261015 P2a — tiêu đề thẻ xem thử của lệnh `restoreSceneMetadata`: chỉ đổi metadata cảnh (action/beats giữ nguyên) nên "N cảnh đổi" nhìn như không có gì đổi (bài học F62).
 * `metaChanges` = `[{ scenePlanId, fields[] }]` do BE trả. Không có ⇒ null.
 */
export const metaChangesHeadline = (metaChanges: unknown): string | null => {
  if (!Array.isArray(metaChanges) || !metaChanges.length) return null;
  const fields = metaChanges.reduce((n: number, c: any) => n + (Array.isArray(c?.fields) ? c.fields.length : 0), 0);
  return `Khôi phục ${fields} trường ở ${metaChanges.length} cảnh`;
};

/**
 * 261015 P2b-3 — tiêu đề thẻ xem thử của lệnh `acceptPlantWritten`: chỉ ghi biên lai (cảnh KHÔNG đổi). `plantAcceptance` = `{ beatId, plant, reason }` do BE trả. Không dùng được ⇒ null.
 */
export const plantAcceptanceHeadline = (pa: unknown): string | null => {
  if (!pa || typeof pa !== 'object') return null;
  const { plant, beatId } = pa as { plant?: unknown; beatId?: unknown };
  const p = str(plant).replace(/\s+/g, ' ');
  const id = str(beatId);
  if (p) return `Chấp nhận: cảnh đã có chi tiết gieo «${p.length > 80 ? `${p.slice(0, 80).trimEnd()}…` : p}» (cảnh không đổi)`;
  return id ? `Chấp nhận: cảnh đã có chi tiết gieo của ${id} (cảnh không đổi)` : null;
};

/** Nhãn nhỏ cạnh tin trợ lý. `options`/`answer`/`change`/null ⇒ không nhãn. */
export const interactionTag = (it: Pick<AssistantInteraction, 'status' | 'capability'>): null | 'Chưa làm được' | 'Sai bước' | 'AI lỗi' => {
  if (it.status === 'FAILED') return 'AI lỗi';
  if (it.capability === 'cannot') return 'Chưa làm được';
  if (it.capability === 'redirect') return 'Sai bước';
  return null;
};

export interface ActionView {
  label: string;
  costTag: 'Miễn phí' | 'Dùng AI' | 'Tốn AI';
  /** Cần Popconfirm nêu nhãn chi phí + gửi `confirm:true`. */
  confirm: boolean;
  enabled: boolean;
  /** Chữ thay nút khi hành động không còn bấm được; null khi còn SUGGESTED. */
  doneText: string | null;
}

const COST_TAG: Record<string, ActionView['costTag']> = { free: 'Miễn phí', ai: 'Dùng AI', money: 'Tốn AI' };

export const actionView = (action: AssistantAction, busy: boolean): ActionView => {
  const free = action.cost === 'free';
  let doneText: string | null = null;
  if (action.state === 'EXECUTED') doneText = 'Đã chạy';
  else if (action.state === 'EXECUTING') doneText = 'Đang chạy…';
  else if (action.state === 'FAILED') doneText = friendlyJobNote(action.result?.error) ? `Không chạy được: ${friendlyJobNote(action.result?.error)}` : 'Không chạy được';
  else if (action.state === 'STALE') doneText = 'Không còn khả dụng — hỏi lại trợ lý';
  return {
    label: action.label, costTag: COST_TAG[action.cost] ?? 'Dùng AI', confirm: !free,
    enabled: action.state === 'SUGGESTED' && (free || !busy), doneText,
  };
};

// ── tải dữ liệu / poll ───────────────────────────────────────────────────────────────────────

/** F19 — lượt RUNNING lâu hơn ngần này (theo `createdAt`) mà BE vẫn chưa đóng ⇒ coi là kẹt (BE khởi động lại giữa chừng): mở khoá ô gửi, báo "bị gián đoạn". */
export const RUNNING_STALE_MS = 5 * 60 * 1000;

export const isStaleRunning = (it: Pick<AssistantInteraction, 'status' | 'createdAt'>, now: number = Date.now()): boolean => {
  if (it.status !== 'RUNNING') return false;
  const t = Date.parse(it.createdAt);
  return Number.isFinite(t) && now - t > RUNNING_STALE_MS;
};

/** Có lượt RUNNING CÒN TRONG HẠN (khoá ô gửi). Lượt kẹt không tính. */
export const hasLiveRunning = (t: Pick<ThreadDetail, 'interactions'> | null | undefined, now: number = Date.now()): boolean =>
  !!t && t.interactions.some((it) => it.status === 'RUNNING' && !isStaleRunning(it, now));

export const shouldPollThread = (t: Pick<ThreadDetail, 'interactions'> | null | undefined, now: number = Date.now()): boolean =>
  !!t && t.interactions.some((it) => (it.status === 'RUNNING' && !isStaleRunning(it, now)) || it.blocks.some((b) => b.state === 'PREVIEW_RUNNING'));

const INTERRUPT_RE = /gián đoạn|quá hạn|khởi động lại|interrupt|restart/i;
/** F19 — câu cho lượt FAILED / kẹt. Không có lý do riêng (hoặc lý do là "gián đoạn") ⇒ câu chung; lỗi AI cụ thể thì giữ lý do. */
export const interruptedText = (it: Pick<AssistantInteraction, 'status' | 'error'>): string => {
  const e = str(it.error);
  if (it.status === 'FAILED' && e && !INTERRUPT_RE.test(e)) {
    // F40: lỗi kỹ thuật (parse JSON, timeout…) ⇒ câu chung, chữ thô chỉ ở `interruptedDetail` (tooltip "Chi tiết kỹ thuật").
    return looksTechnical(e) ? `AI không trả lời được lượt này — gửi lại; ${GENERIC_JOB_ERROR_HINT.replace(/^thử lại; /, '')}.` : `AI không trả lời được lượt này — gửi lại. ${friendlyJobNote(e)}`;
  }
  return 'Lượt bị gián đoạn — gửi lại.';
};

/** F40 — chữ thô của lỗi kỹ thuật (chỉ cho tooltip "Chi tiết kỹ thuật"); không kỹ thuật / không có ⇒ undefined. */
export const interruptedDetail = (it: Pick<AssistantInteraction, 'status' | 'error'>): string | undefined => {
  const e = str(it.error);
  return it.status === 'FAILED' && e && !INTERRUPT_RE.test(e) && looksTechnical(e) ? e : undefined;
};

const GENERIC_FAIL_MESSAGE_RE = /AI không trả lời được lượt này/i;
/**
 * F40 — lượt FAILED chỉ hiện MỘT dòng lỗi (+ "Gửi lại"): bỏ nhãn "AI lỗi" và câu xin lỗi chung do BE tự ghi (trùng nghĩa với dòng đó).
 * Tin nhắn trợ lý có nội dung khác vẫn giữ.
 */
export const visibleAssistantMessages = <M extends { role: string; text: string }>(it: Pick<AssistantInteraction, 'status'> & { messages: M[] }): M[] =>
  it.messages.filter((m) => m.role === 'assistant' && !(it.status === 'FAILED' && GENERIC_FAIL_MESSAGE_RE.test(m.text)));
export const showInteractionTag = (it: Pick<AssistantInteraction, 'status'>): boolean => it.status !== 'FAILED';

// ── F40: chip dạng nhãn ngắn / gộp ───────────────────────────────────────────

export interface ChipGroup { ids: string[]; label: string; title: string; bad: boolean }
const NAME_CHIP_RE = /^(?:⛔\s*)?Tên\s+[“"'‘]?(.+?)[”"'’]?\s+xuất hiện\s+(\d+)\s+lần.*ánh xạ/i;
const CHIP_MAX = 48;

/** Nhãn ngắn một chip: "Tên "Derek" xuất hiện 6 lần … chưa có ánh xạ" ⇒ "Derek ×6"; chữ dài khác cắt 48 ký tự (đầy đủ ở `title`). */
export const shortChipLabel = (label: string): string => {
  const m = NAME_CHIP_RE.exec(label.trim());
  if (m) return `${m[1]} ×${m[2]}`;
  const t = label.trim();
  return t.length > CHIP_MAX ? `${t.slice(0, CHIP_MAX - 1).trimEnd()}…` : t;
};

/** Gộp chip cho ô soạn / bong bóng: từ `minGroup` chip "tên chưa ánh xạ" trở lên ⇒ MỘT chip "N tên chưa ánh xạ"; còn lại nhãn ngắn. Giữ thứ tự xuất hiện. */
export const groupChips = (items: Array<{ id: string; label: string; bad?: boolean }>, minGroup = 4): ChipGroup[] => {
  const names = items.filter((c) => NAME_CHIP_RE.test(c.label.trim()));
  const group = names.length >= minGroup;
  const out: ChipGroup[] = [];
  let placed = false;
  for (const c of items) {
    if (group && NAME_CHIP_RE.test(c.label.trim())) {
      if (placed) continue;
      placed = true;
      out.push({ ids: names.map((n) => n.id), label: `${names.length} tên chưa ánh xạ`, title: names.map((n) => shortChipLabel(n.label)).join(', '), bad: names.some((n) => !!n.bad) });
    } else out.push({ ids: [c.id], label: shortChipLabel(c.label), title: c.label, bad: !!c.bad });
  }
  return out;
};

const ONLY_CHIPS = '(chỉ tick lỗi)';
/** F19 — nút "Gửi lại": đưa chữ người đã gõ + lỗi đã tick của lượt đó về ô soạn (KHÔNG tự gửi — gửi là tốn AI). */
export const retryDraftOf = (it: Pick<AssistantInteraction, 'messages' | 'issueIds'>): { text: string; selected: string[] } => {
  const t = str(it.messages.find((m) => m.role === 'user')?.text);
  return { text: t === ONLY_CHIPS ? '' : t, selected: [...(it.issueIds ?? [])] };
};

/** F24b — nút "Lịch sử": chưa tải xong thì KHÔNG tắt (cú bấm đầu bị nuốt vì nút còn tắt) mà mở menu "Đang tải…"; chỉ tắt khi đã tải xong và thật sự rỗng. */
export const historyMenuView = (threadCount: number, loading: boolean): { buttonDisabled: boolean; showLoadingItem: boolean } =>
  threadCount > 0 ? { buttonDisabled: false, showLoadingItem: false } : loading ? { buttonDisabled: false, showLoadingItem: true } : { buttonDisabled: true, showLoadingItem: false };

/** F23 — Tooltip lý do chỉ hiện khi nút bị tắt; nút bật mà có Popconfirm thì tooltip cùng nội dung sẽ đè lên. */
export const disabledTip = (enabled: boolean, why: string | undefined | null): string | undefined => (enabled ? undefined : why ?? undefined);

export const shouldPollScope = (v: Pick<ScopeView, 'relink'> | null | undefined): boolean => v?.relink?.state === 'RUNNING';

/** Giữ luồng đang chọn nếu còn trong danh sách; không thì luồng đầu (API trả mới nhất trước); rỗng ⇒ null. */
export const pickThreadId = (threads: ReadonlyArray<Pick<AssistantThread, 'id'>>, current: string | null | undefined): string | null => {
  if (current && threads.some((t) => t.id === current)) return current;
  return threads.length ? threads[0].id : null;
};

/** `requestId` mỗi lần bấm Gửi. `crypto.randomUUID` chỉ có ở localhost/https ⇒ fallback uuid v4 cho truy cập http qua IP LAN. */
export const newRequestId = (randomUUID: (() => string) | null | undefined = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? () => crypto.randomUUID() : null): string => {
  if (randomUUID) return randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`;
};

// ── lỗi ──────────────────────────────────────────────────────────────────────────────────────

const ASSISTANT_CODE_TEXT: Record<string, string> = {
  THREAD_BUSY: 'Luồng đang có một lượt chưa xong — đợi xong rồi gửi tiếp.',
  STATE_CONFLICT: 'Việc này vừa được xử lý ở nơi khác hoặc đã đổi trạng thái — đã tải lại, xem rồi thử lại nếu cần.',
  ACTION_STALE: 'Hành động này không còn khả dụng lúc này (dữ liệu đã đổi) — hãy hỏi lại trợ lý.',
  CONFIRM_REQUIRED: 'Hành động này tốn AI — cần xác nhận trước khi chạy.',
  UNKNOWN_ISSUE: 'Có lỗi được tick không còn trong danh sách của tập — bỏ tick rồi gửi lại.',
  NO_RELINK_CARD: 'Không còn thẻ nối tập để nối — tải lại rồi xem trạng thái hiện tại.',
  RELINK_NOT_FOUND: 'Không thấy thẻ nối tập này (đã bị thay hoặc không còn) — tải lại để xem thẻ hiện hành.',
  ALREADY_RELINKED: 'Tập kế tiếp đã được nối lại rồi.',
  AI_STILL_RUNNING: 'AI vẫn đang kiểm nối tập — đợi AI xong rồi nối.',
  NEXT_EPISODE_MISSING: 'Tập kế tiếp chưa có kịch bản (hoặc không còn ở DRAFT) nên không nối được.',
  ALREADY_LINKED: 'Tập kế tiếp đã nối khít với đoạn kết hiện tại — không cần nối lại.',
  VERSION_PRUNED: 'Bản trước đã vượt quá 20 phiên bản lưu và bị dọn — không khôi phục tự động được; chọn một phiên bản ở mục Phiên bản.',
  VERSION_MISMATCH: 'Bản trước không còn khớp kịch bản lúc xem thử — không khôi phục tự động được; dùng mục Phiên bản.',
  CONTENT_DIVERGED: 'Kịch bản đã khác bản gốc của đề xuất này — bấm "Xem thử lại" để AI viết thử trên bản hiện tại.',
  EXPECTED_MODIFIED_AT_REQUIRED: 'Thiếu mốc thời gian của kịch bản để so khớp — tải lại trang rồi thử lại.',
  // 261015 P4c-2c-1 (D10): trợ lý ⑤ (Viết lại tập · Xem thử / Áp / Áp lại khối) khi truyện CÓ tổng quan. Lối ra = chỗ sửa (tổng quan ở ②), không phải "hỏi lại trợ lý".
  OVERVIEW_NOT_EFFECTIVE: 'Tổng quan truyện chưa chốt hoặc đã cũ — mở "Tổng quan truyện" ở ② để chốt lại, rồi xem thử lại. Chưa tốn AI.',
  OVERVIEW_CHANGED: 'Tổng quan truyện vừa đổi (trong lúc viết hoặc sau khi xem thử) — chưa ghi gì. Mở "Tổng quan truyện" ở ② kiểm tra rồi bấm "Xem thử lại".',
};

/** Câu riêng của chặng ② (thắng `ASSISTANT_CODE_TEXT` khi `stageKey==='skeleton'`). */
const SKELETON_CODE_TEXT: Record<string, string> = {
  SCRIPT_CHANGED: 'Khung xương đã đổi sau khi áp — khôi phục qua mục Phiên bản.',
  OUTLINE_CHANGED: 'Khung xương vừa bị thay đổi bởi thao tác khác — đã tải lại, xem thử lại rồi thử tiếp.',
  PREVIEW_STALE: 'Xem thử này đã cũ (khung xương đã đổi sau đó) — bấm "Xem thử lại".',
  CONTENT_DIVERGED: 'Khung xương đã khác lúc đề xuất — cần "Xem thử lại".',
  VERSION_MISMATCH: 'Bản trước không còn khớp khung xương lúc xem thử — không khôi phục tự động được; dùng mục Phiên bản.',
  NOTHING_APPLIED: 'Không lệnh nào áp được trên khung xương hiện tại — bấm "Xem thử lại".',
  OUTLINE_MISSING: 'Chưa có khung xương — dựng khung xương trước.',
  JOB_RUNNING: 'Đang có tiến trình dựng/chia tập/bản địa hoá của truyện này chạy — chờ xong rồi thử lại.',
  UNKNOWN_ISSUE: 'Có cảnh báo được tick không còn trong khung xương — bỏ tick rồi gửi lại.',
  HAS_ERRORS: 'Khung xương còn lỗi ERROR — xử lý rồi mới duyệt được.',
  INVALID_SCOPE: 'Phạm vi trò chuyện của bước Khung xương không hợp lệ — tải lại trang.',
};

/** Câu riêng của chặng ④ (thắng `ASSISTANT_CODE_TEXT` khi `stageKey==='split'`). */
const SPLIT_CODE_TEXT: Record<string, string> = {
  SCRIPT_CHANGED: 'Chia tập đã đổi sau khi áp — khôi phục qua mục Phiên bản.',
  OUTLINE_CHANGED: 'Chia tập vừa bị thay đổi bởi thao tác khác — đã tải lại, xem thử lại rồi thử tiếp.',
  PREVIEW_STALE: 'Xem thử này đã cũ (chia tập đã đổi sau đó) — bấm "Xem thử lại".',
  CONTENT_DIVERGED: 'Chia tập đã khác lúc đề xuất — cần "Xem thử lại".',
  VERSION_MISMATCH: 'Bản trước không còn khớp chia tập lúc xem thử — không khôi phục tự động được; dùng mục Phiên bản.',
  NOTHING_APPLIED: 'Không lệnh nào áp được trên chia tập hiện tại — bấm "Xem thử lại".',
  OUTLINE_MISSING: 'Chưa có khung xương — dựng khung xương trước.',
  EPISODES_MISSING: 'Chưa chia tập — chia tập trước rồi trò chuyện.',
  EPISODES_STALE: 'Danh sách tập đã cũ sau khi dựng lại khung xương — chia lại tập trước.',
  EPISODE_LOCKED_WRITTEN: 'Bản này đổi cấu trúc tập đã viết/đã đẩy — không làm được (chỉ sửa chữ tóm tắt/móc).',
  INVALID_EPISODES: 'Tập không hợp lệ để viết lại (tập đã viết bị khoá, hoặc quá 12 tập).',
  SOURCE_RANGE_STALE: 'Nội dung chương nguồn đã đổi kể từ lúc chia — chia lại tập trước khi viết lại tóm tắt.',
  REWRITE_INCOMPLETE: 'AI viết lại chưa đủ các tập — bấm "Xem thử lại".',
  JOB_RUNNING: 'Đang có tiến trình dựng/chia tập/bản địa hoá/viết lại tóm tắt của truyện này chạy — chờ xong rồi thử lại.',
  UNKNOWN_ISSUE: 'Có mục được tick không còn trong danh sách chia tập — bỏ tick rồi gửi lại.',
  INVALID_SCOPE: 'Phạm vi trò chuyện của bước Chia tập không hợp lệ — tải lại trang.',
  // 261015 P4c-2b (D9b): xem thử viết lại tập bị từ chối TRƯỚC khi gọi AI
  OVERVIEW_NOT_EFFECTIVE: 'Tổng quan truyện chưa chốt hoặc đã cũ — mở "Tổng quan truyện" ở ② để chốt lại, rồi xem thử lại. Chưa tốn AI.',
};

/** Câu riêng của chặng ③ (thắng `ASSISTANT_CODE_TEXT` khi `stageKey==='localize'`). */
const LOCALIZE_CODE_TEXT: Record<string, string> = {
  SCRIPT_CHANGED: 'Đã có thay đổi mới hơn sau khi áp — khôi phục qua mục Phiên bản (kịch bản sẽ đổi tên ngược).',
  OUTLINE_CHANGED: 'Bản địa hoá vừa bị thay đổi bởi thao tác khác — đã tải lại, xem thử lại rồi thử tiếp.',
  PREVIEW_STALE: 'Xem thử này đã cũ (tên / từ vựng đã đổi sau đó) — bấm "Xem thử lại".',
  CONTENT_DIVERGED: 'Bản địa hoá đã khác lúc đề xuất — bấm "Xem thử lại".',
  VERSION_MISMATCH: 'Bản trước không còn khớp bản địa hoá lúc xem thử — không khôi phục tự động được; dùng mục Phiên bản.',
  NOTHING_APPLIED: 'Không lệnh nào áp được trên bản địa hoá hiện tại — bấm "Xem thử lại".',
  OUTLINE_MISSING: 'Chưa có khung xương — dựng khung xương trước.',
  JOB_RUNNING: 'Đang có tiến trình dựng/chia tập/bản địa hoá của truyện này chạy — chờ xong rồi thử lại.',
  UNKNOWN_ISSUE: 'Có mục được tick không còn trong danh sách của bước — bỏ tick rồi gửi lại.',
  HAS_ERRORS: 'Có lỗi chặn (tên trùng, trùng nguyên tác, tên người nổi tiếng…) — sửa rồi xem thử lại.',
  INVALID_SCOPE: 'Phạm vi trò chuyện của bước Bản địa hoá không hợp lệ — tải lại trang.',
  LOCALIZE_VERSION_NOT_FOUND: 'Không thấy phiên bản bản địa hoá này — tải lại danh sách phiên bản.',
};

/** Câu riêng của chặng ① (thắng `ASSISTANT_CODE_TEXT` khi `stageKey==='source'`). Mã P4a giữ nguyên. */
const SOURCE_CODE_TEXT: Record<string, string> = {
  PREVIEW_STALE: 'Xem thử này đã cũ (chương đã đổi sau đó) — bấm "Xem thử lại".',
  NOTHING_APPLIED: 'Không áp được lệnh trên chương hiện tại — bấm "Xem thử lại".',
  VERSION_NOT_LATEST: 'Đã có thao tác khác sau thao tác này — khôi phục từ mục Phiên bản chương.',
  VERSION_MISMATCH: 'Khối này không còn phiên bản để khôi phục tự động — dùng mục Phiên bản chương.',
  SOURCE_CHANGED: 'Chương vừa bị thay đổi bởi thao tác khác — đã tải lại, thử lại.',
  CHAPTERS_MISSING: 'Truyện chưa có chương nào — nạp truyện trước.',
  NOTHING_TO_APPLY: 'Tải lại ra đúng nội dung đang có — không có gì để áp.',
  JOB_RUNNING: 'Đang có tác vụ chạy (nạp/quét/dựng khung xương/bản địa hoá) — chờ xong rồi thử lại.',
  CHAPTER_LOCKED_BY_SCRIPT: 'Chương thuộc tập đã viết/đã đẩy — không đổi được.',
  CHAPTER_CHANGED: 'Chương vừa bị thay đổi bởi thao tác khác — xem thử lại.',
  CHAPTERS_CHANGED: 'Chương vừa bị thay đổi bởi thao tác khác — xem thử lại.',
  CONTENT_SHA_MISMATCH: 'Nội dung tải về không khớp bản xem thử — bấm "Xem thử lại".',
  NO_NEXT_CHAPTER: 'Chương này là chương cuối — không có chương sau để gộp.',
  NOT_A_CUT_CANDIDATE: 'Điểm tách không còn hợp lệ — bấm "Xem thử lại" hoặc hỏi lại trợ lý.',
  LINK_CONFLICT: 'Đã có chương khác dùng cùng link — không tạo lại được chương này.',
  UNKNOWN_ISSUE: 'Có chương được tick không còn lỗi (đã sửa) — bỏ tick rồi gửi lại.',
  INVALID_SCOPE: 'Phạm vi trò chuyện của bước Nguồn không hợp lệ — tải lại trang.',
};

/** Câu lỗi cho người dùng: mã của khung Trợ lý có câu riêng (không đưa chữ server thô); còn lại rơi về `fixErrorText`. */
export const assistantErrorText = (err: unknown, stageKey: AssistantUiStage = 'script'): string => {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
  // 261015 P4c-1: lỗi tổng quan (Áp khối sửa / nút chốt) ⇒ câu + "hỏi lại trợ lý" (đề xuất cũ không còn khớp dữ liệu).
  if (stageKey === 'skeleton' && code && (code.startsWith('OVERVIEW_') || code === 'EXPECTED_MODIFIED_AT_REQUIRED')) return overviewErrorView(err, 'Không thực hiện được', { via: 'assistant' }).text;
  if (stageKey === 'skeleton' && code && SKELETON_CODE_TEXT[code]) return SKELETON_CODE_TEXT[code];
  if (stageKey === 'split' && code && SPLIT_CODE_TEXT[code]) return SPLIT_CODE_TEXT[code];
  if (stageKey === 'source' && code && SOURCE_CODE_TEXT[code]) return SOURCE_CODE_TEXT[code];
  if (stageKey === 'localize' && code && LOCALIZE_CODE_TEXT[code]) return LOCALIZE_CODE_TEXT[code];
  if (code && ASSISTANT_CODE_TEXT[code]) return ASSISTANT_CODE_TEXT[code];
  return fixErrorText(err);
};

/** 261015 P4c-2c-1 (D10) — khối cảnh báo có lối ra "Mở Tổng quan truyện" của khung Trợ lý ⑤ (khuôn `overviewBlock` của ④ `SplitStage`). */
export interface AssistantOverviewBlock { text: string; extra: string[] }

/**
 * 261015 P4c-2c-1 (D10) — lỗi của Xem thử / Áp / Áp lại khối / Viết lại tập (qua trợ lý) ở ⑤ là `OVERVIEW_NOT_EFFECTIVE` hoặc `OVERVIEW_CHANGED` ⇒ khối cảnh báo + nút "Mở Tổng quan truyện";
 * mã khác, hoặc chặng khác ⑤ (② có `overviewErrorView` riêng, ④ có câu riêng) ⇒ `null` (chỉ toast như cũ). Chữ = đúng câu `assistantErrorText` đang toast; `extra` = nguyên nhân từng dòng
 * (`details.overviewState`) chỉ cho `OVERVIEW_NOT_EFFECTIVE`. Hàm thuần — quyết định hiện nút nằm ở đây, không ở tsx.
 */
export const assistantOverviewBlock = (err: unknown, stageKey: AssistantUiStage): AssistantOverviewBlock | null => {
  if (stageKey !== 'script') return null;
  const data = (err as { response?: { data?: { code?: unknown; details?: unknown } } } | null | undefined)?.response?.data;
  const code = data?.code;
  if (!isOverviewWriterCode(code)) return null;
  return { text: assistantErrorText(err, stageKey), extra: code === 'OVERVIEW_NOT_EFFECTIVE' ? overviewNotEffectiveLines(data?.details) : [] };
};

/**
 * 261015 P4c-1 (Q18) — lỗi của NÚT HÀNH ĐỘNG do Trợ lý đề xuất. `approve_skeleton` chốt đúng bản người đang xem lúc ĐỀ XUẤT (`baseModifiedAt`): bản đã đổi
 * ⇒ 409 `OUTLINE_CHANGED` / `ACTION_STALE`, hoặc chốt bị chặn ⇒ 409 `OVERVIEW_*` — mọi trường hợp đều nói "hỏi lại trợ lý" (đề xuất cũ không còn đúng),
 * KHÔNG dùng câu "xem thử lại" của khối sửa. Hành động khác rơi về `assistantErrorText`.
 */
export const assistantActionErrorText = (err: unknown, registryId: string, stageKey: AssistantUiStage = 'skeleton'): string => {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
  if (registryId === 'approve_skeleton' && code && (code === 'OUTLINE_CHANGED' || code === 'ACTION_STALE' || code === 'EXPECTED_MODIFIED_AT_REQUIRED' || code.startsWith('OVERVIEW_'))) {
    return overviewErrorView(err, 'Không chốt được', { via: 'assistant' }).text;
  }
  return assistantErrorText(err, stageKey);
};

// ── thẻ nối tập: nút nào bấm được ────────────────────────────────────────────────────────────

/**
 * Cổng cho nút của thẻ nối tập (Y5): thẻ nói "nối được" chưa đủ — còn phải tập N còn DRAFT, tập N+1 còn DRAFT và có `modifiedAt` (CAS), và không bận.
 * `reason` chỉ có khi thẻ vốn cho nối mà bị cổng này tắt (để hiện cạnh nút).
 */
export const relinkActionState = (o: {
  view: Pick<RelinkView, 'canRelink' | 'canRewrite' | 'nextEpisode'>;
  nextScript: Pick<EpisodeScriptRecord, 'status' | 'modifiedAt'> | null | undefined;
  editBlocker: string | null;
  busy: boolean;
  /** E4-P1 — lý do riêng khi `busy` do sửa tay (thay câu mặc định). */
  busyReason?: string | null;
}): { canRelink: boolean; canRewrite: boolean; reason: string | null } => {
  const n = o.view.nextEpisode;
  if (!o.view.canRelink && !o.view.canRewrite) return { canRelink: false, canRewrite: false, reason: null };
  let reason: string | null = null;
  if (o.editBlocker === 'NOT_DRAFT') reason = 'Tập này không còn ở trạng thái DRAFT — không nối được.';
  else if (!o.nextScript) reason = `Tập ${n} chưa có kịch bản — không nối được.`;
  else if (o.nextScript.status !== 'DRAFT') reason = `Tập ${n} không ở trạng thái DRAFT — không nối được.`;
  else if (!str(o.nextScript.modifiedAt)) reason = `Thiếu mốc modifiedAt của tập ${n} — tải lại rồi thử.`;
  else if (o.busy) reason = o.busyReason || 'Đang có việc khác chạy — chờ xong rồi nối.';
  return { canRelink: o.view.canRelink && reason === null, canRewrite: o.view.canRewrite && !o.busy, reason: reason ?? (o.busy && o.view.canRewrite ? (o.busyReason || null) : null) };
};

/**
 * 261015 P4a (D9, Q10) — cổng bấm của nút "Hoàn tác" (thẻ `RELINKED` là phase `done` nên `relinkActionState` trả tắt sớm ⇒ cần cổng riêng).
 * Điều kiện: thẻ do hệ thống tự nối; tập N còn DRAFT; tập N+1 còn DRAFT và có `modifiedAt` (CAS); không bận; và 🔴 race: tự nối ghi N+1 trong nền nên có một nhịp thẻ đã hiện `RELINKED`
 * mà danh sách kịch bản còn bản CŨ ⇒ `nextScript.modifiedAt` < `relinkedAt` (BE lấy `relinkedAt` TRƯỚC khi ghi ⇒ mốc ghi luôn ≥ nó; sai số ±1 ms như `scriptCasWhere`) ⇒ tắt, tránh 409 `SCRIPT_CHANGED`.
 */
export const undoAutoState = (o: {
  view: Pick<RelinkView, 'canUndoAuto' | 'nextEpisode'>;
  nextScript: Pick<EpisodeScriptRecord, 'status' | 'modifiedAt'> | null | undefined;
  editBlocker: string | null;
  busy: boolean;
  busyReason?: string | null;
  /** `card.relinkedAt` (ISO). Vắng ⇒ không có mốc để so. */
  relinkedAt?: string | null;
}): { canUndo: boolean; reason: string | null } => {
  if (!o.view.canUndoAuto) return { canUndo: false, reason: null };
  const n = o.view.nextEpisode;
  let reason: string | null = null;
  if (o.editBlocker === 'NOT_DRAFT') reason = 'Tập này không còn ở trạng thái DRAFT — không hoàn tác được.';
  else if (!o.nextScript) reason = `Tập ${n} chưa có kịch bản — không hoàn tác được.`;
  else if (o.nextScript.status !== 'DRAFT') reason = `Tập ${n} không ở trạng thái DRAFT — không hoàn tác được.`;
  else if (!str(o.nextScript.modifiedAt)) reason = `Thiếu mốc modifiedAt của tập ${n} — tải lại rồi thử.`;
  else if (o.busy) reason = o.busyReason || 'Đang có việc khác chạy — chờ xong rồi hoàn tác.';
  else {
    const at = Date.parse(str(o.relinkedAt));
    const mod = Date.parse(str(o.nextScript.modifiedAt));
    if (Number.isFinite(at) && Number.isFinite(mod) && mod + 1 < at) reason = `Kịch bản tập ${n} chưa cập nhật sau lần tự nối — tải lại một chút rồi thử.`;
  }
  return { canUndo: reason === null, reason };
};

/** Lý do tắt nút "Khôi phục" (E4-P1: `busyReason` = "Đang sửa tay …" thay câu mặc định) (null = bấm được). Tập không nháp ⇒ vẫn XEM được phiên bản, chỉ tắt khôi phục (server cũng chặn). */
export const restoreBlockerText = (editBlocker: string | null | undefined, busy: boolean, busyReason?: string | null): string | null => {
  const b = editBlockerText(editBlocker);
  if (b) return `${b} Khôi phục đang tắt.`;
  if (busy) return busyReason || 'Đang có việc khác chạy — chờ xong rồi khôi phục.';
  return null;
};

/** Cách lỗi nên xử lý sau khi báo: các mã "dữ liệu đã đổi" ⇒ tải lại luồng. */
export const shouldReloadAfterError = (err: unknown): boolean => {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
  return code === 'STATE_CONFLICT' || code === 'PREVIEW_STALE' || code === 'ACTION_STALE' || code === 'THREAD_BUSY'
    || code === 'SCRIPT_CHANGED' || code === 'CONTENT_DIVERGED' || code === 'VERSION_PRUNED' || code === 'OUTLINE_CHANGED' || code === 'NOTHING_APPLIED'
    || code === 'EPISODES_STALE' || code === 'SOURCE_CHANGED' || code === 'VERSION_NOT_LATEST' || code === 'CHAPTER_CHANGED' || code === 'NOTHING_TO_APPLY'
    // 261015 P4a (D9): hoàn tác tự nối — thẻ/kịch bản đang hiện đã cũ.
    || code === 'RELINK_SUPERSEDED' || code === 'NOT_AUTO_RELINK' || code === 'VERSION_NOT_FOUND'
    // 261015 P4c-1: tổng quan đổi (nguồn / khung xương / job) ⇒ thẻ đang hiện đã cũ.
    || (typeof code === 'string' && code.startsWith('OVERVIEW_'))
    // + người bấm "Nối lại" đúng lúc hệ thống vừa tự nối (thẻ đang hiện đã cũ).
    || code === 'ALREADY_RELINKED';
};

// ── hành vi theo chặng (E3-P2b — một chỗ, có test; vỏ không rải `isSkeleton`/`isSplit`) ───────────

/** Câu xác nhận khôi phục phiên bản theo chặng. */
export const restoreConfirmText = (stageKey: AssistantUiStage): string => ({
  script: 'Kịch bản hiện tại được thay bằng bản này; bản hiện tại vẫn còn trong danh sách. Đoạn kết đổi ⇒ AI kiểm nối tập sau.',
  skeleton: 'Khôi phục phần khung xương của bản này; bản địa hoá, chia tập giữ nguyên; khung xương về bản nháp; cờ chia tập không tự tắt.',
  split: 'Khôi phục cách chia tập về bản này — tập đã viết không đổi; bản hiện tại vẫn còn trong danh sách.',
  source: 'Hoàn tác thao tác chương mới nhất: chương quay về trước thao tác này; thao tác vừa gỡ vẫn còn trong danh sách.',
  localize: 'Khôi phục tên / họ / từ vựng / bối cảnh phim về bản này: kịch bản đã viết sẽ ĐỔI TÊN NGƯỢC và đẩy lại sang Chapters (chương có Film Plan / đã sửa tay giữ nguyên). Bản hiện tại vẫn còn trong danh sách.',
} as Record<AssistantUiStage, string>)[stageKey];

/** Câu khi khung không có gì để nói (outline/tập mất). */
export const goneText = (stageKey: AssistantUiStage, episode: number): string =>
  stageKey === 'source' ? 'Truyện chưa có chương nào để trò chuyện — nạp truyện trước.'
    : stageKey === 'skeleton' ? 'Chưa có khung xương để trò chuyện — dựng khung xương trước.'
    : stageKey === 'split' ? 'Chưa chia tập để trò chuyện — chia tập trước.'
    : stageKey === 'localize' ? 'Chưa có khung xương để trò chuyện về bản địa hoá — dựng khung xương trước.'
      : `Tập ${episode} không còn kịch bản để trò chuyện.`;

/** F17 — mã 404 nghĩa là "chặng này chưa có gì để trò chuyện" (chưa có chương / khung xương / chia tập / tập) ⇒ trạng thái rỗng, KHÔNG phải lỗi. */
const GONE_CODES = ['EPISODE_NOT_FOUND', 'STAGE_NOT_SUPPORTED', 'CHAPTERS_MISSING', 'OUTLINE_MISSING', 'EPISODES_MISSING'];
export const isGoneCode = (code: string | undefined): boolean => !!code && GONE_CODES.includes(code);

/**
 * F10 — khung đang hiện "chưa có gì để trò chuyện" (server 404: chưa có chương / tập mất) mà dữ liệu chung vừa đổi (`dataVersion` tăng sau nạp/quét xong, mọi thao tác ghi)
 * ⇒ tải lại scope. Không gone ⇒ không đụng (đã có poll riêng); version không đổi ⇒ không lặp.
 */
/** F17: `gone` truyền vào = đang ở trạng thái rỗng HOẶC lỗi tải (đã thử lại thì không tốn gì, chỉ GET). */
export const shouldRefetchWhenGone = (gone: boolean, hasKey: boolean, lastVersion: number, version: number): boolean =>
  gone && hasKey && lastVersion !== version;

/** Chặng có chip kiểu vi phạm (② ④) — khác chip lỗi tập của ⑤. */
export const usesViolationChips = (stageKey: AssistantUiStage): boolean => stageKey !== 'script';

/** Sau `POST preview`: có job nền (202) ⇒ tải lại số liệu để dải job + khoá hiện ngay và báo "đang viết thử"; đồng bộ (`sync`) ⇒ không. */
export const previewFollowUp = (stageKey: AssistantUiStage, r: Pick<PreviewStarted, 'sync' | 'jobId'> | null | undefined): { refresh: boolean; message: string | null } => {
  if (r?.sync) return { refresh: false, message: null };
  return { refresh: true, message: stageKey === 'split' ? 'AI đang viết lại tóm tắt các tập (nền, vài chục giây đến vài phút) — kết quả hiện ở khối này.' : 'Đang viết thử các cảnh đích (vài chục giây)…' };
};

const touchedText = (touched: readonly number[] | undefined, structural: boolean | undefined): string =>
  structural && touched?.length ? ` Tập ${touched.join(', ')} được đánh dấu "Cần chấm lại sau khi sửa".` : '';

/** Thông báo sau Áp / Áp lại / Khôi phục (không gồm nối tập của ⑤ — vỏ xử lý riêng). */
/** Cảnh báo vàng sau Áp / Áp lại / Khôi phục ③ khi ĐẨY LẠI sang Chapters lỗi (phần đã ghi giữ nguyên). null = không có. */
export const handoffWarningOf = (stageKey: AssistantUiStage, r: { handoffError?: string } | null | undefined): string | null =>
  stageKey === 'localize' && r?.handoffError ? `Đã áp, nhưng đẩy lại Chapters lỗi: ${redactMoney(r.handoffError)}` : null;

export const appliedMessage = (stageKey: AssistantUiStage, r: { versionNo?: number; episodesStale?: boolean; staleBefore?: boolean; touched?: number[]; structural?: boolean; sourceImpact?: unknown; keptApproval?: boolean }, verb: 'apply' | 'reapply'): string | null => {
  if (stageKey === 'skeleton') {
    // 261015 P2b-1 (C5/D8): sửa chỉ-metadata có neo GIỮ "đã duyệt" — không nói "về bản nháp" khi BE báo `keptApproval`.
    return verb === 'apply'
      ? `Đã áp (v${r.versionNo}).${r.episodesStale && r.staleBefore === false ? ' Danh sách tập cần chia lại ở ④.' : ''} ${r.keptApproval ? 'Khung xương giữ trạng thái đã duyệt' : 'Khung xương về bản nháp'}; bản cũ còn ở Phiên bản.`
      : `Đã áp lại (v${r.versionNo}).${r.keptApproval ? ' Khung xương giữ trạng thái đã duyệt.' : ''}`;
  }
  if (stageKey === 'localize') {
    const eps = r.touched ?? [];
    return `${verb === 'apply' ? 'Đã áp' : 'Đã áp lại'} (v${r.versionNo}).${eps.length ? ` Đã đổi tên trong kịch bản tập ${eps.join(', ')} và đẩy lại Chapters.` : ''} Bản cũ còn ở Phiên bản.`;
  }
  if (stageKey === 'split') return `${verb === 'apply' ? 'Đã áp' : 'Đã áp lại'} (v${r.versionNo}).${touchedText(r.touched, r.structural)} Bản cũ còn ở Phiên bản.`;
  if (stageKey === 'source') {
    const lines = impactLines((r as { sourceImpact?: Parameters<typeof impactLines>[0] }).sourceImpact);
    return `Đã áp (v${r.versionNo}).${lines.length ? ` ${lines.join(' · ')}.` : ''} Hoàn tác được ở khối này hoặc mục Phiên bản chương.`;
  }
  return null;
};

export const restoredMessage = (stageKey: AssistantUiStage, fromNo: number, toNo: number): string | null =>
  stageKey === 'skeleton' ? `Đã khôi phục v${fromNo} (thành v${toNo}). Khung xương về bản nháp; cờ chia tập không tự tắt.`
    : stageKey === 'split' ? `Đã khôi phục v${fromNo} (thành v${toNo}). Tập đã viết không đổi.`
      : stageKey === 'localize' ? `Đã khôi phục v${fromNo} (thành v${toNo}). Kịch bản đã viết đổi tên ngược và đẩy lại Chapters.`
      : stageKey === 'source' ? `Đã hoàn tác thao tác v${fromNo} (ghi thành v${toNo}). Quyển/danh sách tập cần quét/chia lại nếu thao tác trước đã đánh dấu.`
      : null;

/** Ô "Nhờ AI viết lại" của ④: các tập `textReview` có thể viết lại (> M, tối đa 12) — thứ tự tăng dần. */
export const rewritableEpisodes = (episodes: ReadonlyArray<{ ep: number; textReview?: boolean }>, lockedThrough: number, max = 12): number[] =>
  episodes.filter((e) => e.textReview && e.ep > lockedThrough).map((e) => e.ep).sort((a, b) => a - b).slice(0, max);

/** Câu gợi ý cho lỗi chia tập ở ④ (khuôn `skeletonIssueQuestion`). */
export const splitIssueQuestion = (chip: Pick<SkeletonIssueChip, 'gate' | 'message'>): string =>
  `Chia tập có cảnh báo "${str(chip.message).slice(0, 300)}". Giải thích ngắn vì sao và nên sửa thế nào (nêu rõ tập nào).`;

/** 261015 P5a (D1): nút "Nhờ AI sửa (tốn AI)" của mỗi cảnh báo ④ — gửi luôn (`autoSend:'fix'`); `suggestedText` là câu hỏi dự phòng khi chip đã mất / ô soạn có chữ. */
export const splitAssistAction = (chip: Pick<SkeletonIssueChip, 'id' | 'gate' | 'message'>): { label: typeof FIX_LABEL; intent: AssistantRequest } => ({
  label: FIX_LABEL,
  intent: { stageKey: 'split', issueIds: [chip.id], suggestedText: splitIssueQuestion(chip), autoSend: 'fix' },
});
